import React, { useState, useEffect } from "react";
import axios from "axios";
import { useSelector } from "react-redux";
import CustomButton from "../../components/Helpers/CustomButton";

export default function ReportRAA() {
    const { properties } = useSelector((state) => state.properties);
    const [rows, setRows] = useState([]);
    const [loading, setLoading] = useState(false);
    const [exporting, setExporting] = useState(false);
    const [propertyId, setPropertyId] = useState(0);

    const buttonClass =
        "inline-flex items-center px-6 py-2.5 rounded-lg border text-sm font-medium focus:z-10 focus:outline-none border-primary bg-primary text-white hover:bg-primary_hover focus:ring-primary focus:ring-2 focus:ring-offset-2 transition duration-150 ease-in-out shadow-sm";

    const isMissing = (value) => value === null || value === undefined || String(value).trim() === "";
    const rowMissing = (row) => isMissing(row.raa_override) || isMissing(row.raa_payment_type);

    // Show bookings missing custom data first so they're easy to find and fix
    const sortedRows = [...rows].sort((a, b) => Number(rowMissing(b)) - Number(rowMissing(a)));

    useEffect(() => {
        loadReport();
    }, [propertyId]);

    async function loadReport() {
        setLoading(true);
        try {
            const response = await axios({
                method: "post",
                url: `${process.env.REACT_APP_API_URL}reports/raa`,
                data: { property_id: propertyId },
            });
            setRows(response.data || []);
        } catch (error) {
            console.log(error);
            alert("Failed to load RAA report");
        } finally {
            setLoading(false);
        }
    }

    async function exportReport() {
        setExporting(true);
        try {
            const response = await axios({
                method: "post",
                url: `${process.env.REACT_APP_API_URL}reports/export_raa`,
                responseType: "blob",
                data: { property_id: propertyId },
            });

            const href = URL.createObjectURL(response.data);
            const link = document.createElement("a");
            link.href = href;
            link.setAttribute("download", "RAA.xlsx");
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            URL.revokeObjectURL(href);
        } catch (error) {
            console.log(error);
            alert("Failed to export RAA report");
        } finally {
            setExporting(false);
        }
    }

    return (
        <div className="h-full w-auto bg-gray-100 my-6 xl:my-0 xl:mx-4 rounded-3xl flex-1 p-8">
            <div className="flex justify-between items-center mb-8">
                <h1 className="text-3xl font-bold text-gray-900">RAA Report</h1>
                <CustomButton className={buttonClass} text="Export to Excel" onClick={() => exportReport()} loading={exporting} />
            </div>

            <div className="bg-white rounded-xl shadow-sm p-6 mb-6">
                <label className="block text-sm font-medium text-gray-700 mb-2">Property</label>
                <select
                    className="block w-full max-w-md border border-gray-300 rounded-lg shadow-sm py-2 px-3 focus:outline-none focus:ring-primary focus:border-primary sm:text-sm"
                    value={propertyId}
                    onChange={(e) => setPropertyId(e.target.value)}
                >
                    <option value={0}>All Properties</option>
                    {properties &&
                        properties.length > 0 &&
                        properties.map((p) => (
                            <option key={p.value} value={p.value}>
                                {p.label}
                            </option>
                        ))}
                </select>
            </div>

            <div className="bg-white rounded-xl shadow-sm p-6">
                {loading ? (
                    <p className="text-gray-600">Loading…</p>
                ) : rows.length === 0 ? (
                    <p className="text-gray-600">No matching bookings found.</p>
                ) : (
                    <div className="overflow-x-auto">
                        <p className="mb-4 text-sm text-gray-500">{rows.length} booking(s)</p>
                        <table className="min-w-full divide-y divide-gray-200">
                            <thead>
                                <tr>
                                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Booking Reference</th>
                                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Bed</th>
                                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Unit Type</th>
                                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Student Type</th>
                                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">RAA Override</th>
                                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">RAA Payment Type</th>
                                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">RAA Payment Amount</th>
                                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Name</th>
                                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Surname</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-200">
                                {sortedRows.map((row, index) => (
                                    <tr key={index} className="hover:bg-gray-50">
                                        <td className="px-4 py-3 text-sm text-gray-900">{row.booking_reference}</td>
                                        <td className="px-4 py-3 text-sm text-gray-900">{row.bed_code}</td>
                                        <td className="px-4 py-3 text-sm text-gray-900">{row.unit_type}</td>
                                        <td className="px-4 py-3 text-sm text-gray-900">{row.student_type}</td>
                                        <td className="px-4 py-3 text-sm text-gray-900">{row.raa_override}</td>
                                        <td className="px-4 py-3 text-sm text-gray-900">{row.raa_payment_type}</td>
                                        <td className="px-4 py-3 text-sm text-gray-900">
                                            {row.raa_payment_amount !== null && row.raa_payment_amount !== undefined
                                                ? `R ${Number(row.raa_payment_amount).toLocaleString("en-ZA", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
                                                : ""}
                                        </td>
                                        <td className="px-4 py-3 text-sm text-gray-900">{row.name}</td>
                                        <td className="px-4 py-3 text-sm text-gray-900">{row.surname}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>
        </div>
    );
}
